/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import { faMinus } from "@fortawesome/pro-regular-svg-icons";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { RootState } from "../../../../reducers";
import useConfig from "../../../../actions/Tenants/config/configHook";
import { useDeleteLayerLocationHistoryMutation } from "../../../../hooks/mutations/useLayerMutations";
import { useLayerLocationHistory, useOfflineMode, useRouteLayer } from "../../../../hooks/useCurrentEntities";
import { useTenantFacilityLocations } from "../../../../hooks/useFacilityLocationQueries";
import { isFilledArray } from "../../../../utils/getFilledArrayOrDefault";
import { resolveTenantPath } from "../../../../utils/tenantUrl";
import { Button } from "../../../Helper/Button";
import { Loading } from "../../../Helper/Loading";
import AvosModal from "../../Helper/Modal";
import { LOCATION_TYPES } from "../../Tenants/TenantEditFacilityLocationsForm";
import { formatDate } from "../fields/formatters";

type LocationType = (typeof LOCATION_TYPES)[keyof typeof LOCATION_TYPES];

type LayerLocationHistoryItem = {
    id?: string;
    layer_id: string;
    stage: string;
    location_id?: string | null;
    created: string;
    // When API includes expanded location info (some endpoints do)
    type?: LocationType;
    label?: string;
};

export default function LayerLocationHistory() {
    const layer = useRouteLayer();
    const config = useConfig();
    const [modalOpen, setModal] = useState(false);
    const inOfflineMode = useOfflineMode();

    const { data: locationsData, isLoading } = useTenantFacilityLocations(config.tenant_id, { offset: 0, limit: 100000, type: Object.values(LOCATION_TYPES).join(",") });
    const { data: locationHistory = [], isLoading: locationHistoryLoading, isFetched: locationHistoryFetched } = useLayerLocationHistory(config.tenant_id, layer?.id);
    const locations = locationsData?.results ?? [];

    const layer_config = config.get_layer_config(layer);
    const displayStage = (stage: string) => config.get_stage(stage)?.text;

    useEffect(() => {
        // Close the modal if the latest history item is removed
        if (locationHistory && locationHistory.length === 0) {
            setModal(false);
        }
    }, [locationHistory]);

    // Only show loading on initial load (no cached results yet).
    if (isLoading && locations.length === 0) {
        return <Loading />;
    }
    if (locationHistoryLoading) {
        return <Loading />;
    }

    const locationHistoryWithLabels = locationHistory?.map((i) => {
        const location = locations.find((l) => l.location_id === i.location_id);
        // * Older location history items don't have a location_id, so we use the stage
        const stage = displayStage(i.stage);
        return {
            ...i,
            label: location?.label || stage,
            type: location?.type,
            value: formatDate(i.created),
        };
    });

    return (
        <div className="pt-4">
            {/* Not fetched offline; without this an empty block reads as "never allocated" */}
            {inOfflineMode && !locationHistoryFetched && (
                <div className="pb-5">
                    <h6 className="text-bold">Allocation</h6>
                    <div className="text-muted">Not available offline</div>
                </div>
            )}
            {isFilledArray(locationHistoryWithLabels) && (
                <div className="pb-5 cursor-pointer" onClick={() => setModal(true)}>
                    <h6 className="text-bold">Allocation</h6>
                    {locationHistoryWithLabels?.map((i, index) => (
                        <LocationRow
                            key={index}
                            deleteable={false}
                            location={i}
                            tenantId={config.tenant_id}
                            allowRemoveAllocation={layer_config?.allow_remove_allocation ?? false}
                        />
                    ))}
                </div>
            )}

            <AvosModal
                header="Allocations"
                onClose={() => setModal(false)}
                isOpen={modalOpen}
                size="md"
                showSaveButton={false}
            >
                <div>
                    {locationHistoryWithLabels?.map((i, index) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <div key={index} className="py-2">
                            <LocationRow
                                deleteable
                                location={i}
                                tenantId={config.tenant_id}
                                allowRemoveAllocation={layer_config?.allow_remove_allocation ?? false}
                            />
                        </div>
                    ))}
                </div>
            </AvosModal>
        </div>
    );
}

function LocationLink({ location }: { location: LayerLocationHistoryItem }) {
    const tenantId = useSelector((state: RootState) => state.auth.user?.tenant?.tenant_id);

    if (!location.location_id) {
        return <span>{location.label}</span>;
    }

    switch (location.type) {
    case LOCATION_TYPES.RIPENING_ROOM:
    case LOCATION_TYPES.STORAGE_ROOM:
        return <Link to={resolveTenantPath(`/ripening-room/${location.location_id}`, tenantId)}>{location.label}</Link>;
    default:
        return <span>{location.label}</span>;
    }
}

function LocationRow({
    location,
    deleteable,
    allowRemoveAllocation,
    tenantId,
}: {
    location: LayerLocationHistoryItem;
    deleteable: boolean;
    allowRemoveAllocation: boolean;
    tenantId: string;
}) {
    const deleteLocationHistoryMutation = useDeleteLayerLocationHistoryMutation();

    return (
        <div className="d-sm-flex align-items-center py-1">
            {allowRemoveAllocation && deleteable && (
                <div className="pe-2">
                    <Button
                        onClick={() => deleteLocationHistoryMutation.mutate({ tenantId, layerId: location.layer_id, history: location })}
                        color="danger"
                        outline
                        size="sm"
                        icon={faMinus}
                    />
                </div>
            )}
            <div className="text-muted pe-2" css={css`width: 15rem;`}>
                <LocationLink location={location} />
            </div>
            <div className="ms-auto text-end">{formatDate(location.created)}</div>
        </div>
    );
}

